import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfYear,
  formatMonthShort,
  formatYear,
  startOfDay,
  startOfMonth,
  startOfYear,
} from "@/utils/date.utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { DateRange } from "react-day-picker";

export type DatePresetKey =
  | "today"
  | "last7"
  | "last30"
  | "monthToDate"
  | "lastMonth"
  | "twoMonthsAgo"
  | "yearToDate"
  | "lastYear"
  | "twoYearsAgo";

export type DatePreset = {
  key: DatePresetKey;
  label: MessageDescriptor;
  getRange: () => DateRange;
};

/**
 * Rolling ranges are inclusive and end today (end-of-day).
 * Example: "Last 7 days" includes today + previous 6 days.
 */
function rollingDays(days: number): DateRange {
  const to = endOfDay(new Date());
  const from = startOfDay(addDays(new Date(), -(days - 1)));
  return { from, to };
}

function todayRange(): DateRange {
  const now = new Date();
  return { from: startOfDay(now), to: endOfDay(now) };
}

function monthToDate(): DateRange {
  const now = new Date();
  return { from: startOfMonth(now), to: endOfDay(now) };
}

function fullMonth(monthsAgo: number): DateRange {
  const base = addMonths(new Date(), -monthsAgo);
  return { from: startOfMonth(base), to: endOfMonth(base) };
}

function yearToDate(): DateRange {
  const now = new Date();
  return { from: startOfYear(now), to: endOfDay(now) };
}

function fullYear(yearsAgo: number): DateRange {
  const base = new Date();
  base.setFullYear(base.getFullYear() - yearsAgoSafe(yearsAgo));
  return { from: startOfYear(base), to: endOfYear(base) };
}

function yearsAgoSafe(n: number) {
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

export function buildDatePresets(): {
  rolling: DatePreset[];
  months: DatePreset[];
  years: DatePreset[];
} {
  const now = new Date();
  const lastMonthDate = addMonths(now, -1);
  const twoMonthsAgoDate = addMonths(now, -2);

  const lastYearDate = new Date(now);
  lastYearDate.setFullYear(now.getFullYear() - 1);

  const twoYearsAgoDate = new Date(now);
  twoYearsAgoDate.setFullYear(now.getFullYear() - 2);

  return {
    rolling: [
      { key: "today", label: msg`Today`, getRange: todayRange },
      { key: "last7", label: msg`Last 7 days`, getRange: () => rollingDays(7) },
      {
        key: "last30",
        label: msg`Last 30 days`,
        getRange: () => rollingDays(30),
      },
    ],

    months: [
      {
        key: "monthToDate",
        label: msg`Month to date · ${formatMonthShort(now)}`,
        getRange: monthToDate,
      },
      {
        key: "lastMonth",
        label: msg`Last month · ${formatMonthShort(lastMonthDate)}`,
        getRange: () => fullMonth(1),
      },
      {
        key: "twoMonthsAgo",
        label: msg`2 months ago · ${formatMonthShort(twoMonthsAgoDate)}`,
        getRange: () => fullMonth(2),
      },
    ],

    years: [
      {
        key: "yearToDate",
        label: msg`Year to date · ${formatYear(now)}`,
        getRange: yearToDate,
      },
      {
        key: "lastYear",
        label: msg`Last year · ${formatYear(lastYearDate)}`,
        getRange: () => fullYear(1),
      },
      {
        key: "twoYearsAgo",
        label: msg`2 years ago · ${formatYear(twoYearsAgoDate)}`,
        getRange: () => fullYear(2),
      },
    ],
  };
}
